import React, {useEffect, useRef, useState} from "react";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import type {Color} from "../../../../generated/api/base";
import {ColorUtils} from "../../../../utils/ColorUtils";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {useAppStore} from "../../../../StateManager";
import {useClickOutside, useCloseOnKeyboardPress} from "../../utils";
import type {View} from "../../../../data/models/View";
import type {Report} from "../../../../data/models/Report";
import {ColorWindowV5} from "../../colors/ColorWindowV5";
import type {ColorWindowMode} from "../../colors/ColorWindowV5";
import type {IHSLColor} from "../../colors/AdvancedColorPanelV5";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";

interface IViewColorIconSelectorProps {
	readonly view?: View | Report;
	readonly color: Color;
	readonly onColorChange: (newColor: Color) => void;
	readonly onIconClick?: (iconName: string) => void;
	readonly onCloseClick: () => void;
	readonly isOpen: boolean;
	readonly elementDivRef: React.RefObject<HTMLDivElement>;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly verticalAlignment?: VerticalAlignment;
	readonly offsetX?: number;
	readonly offsetY?: number;
	readonly panelWidth?: number;
	readonly isMiniColorPalette?: boolean;
}

const hsl2hex = (hsl: IHSLColor): Color => {
	const hex = ColorUtils.hsl2hex(hsl.hue, hsl.saturation, hsl.lightness, "string") as string;

	return {
		hex: hex,
		transparency: hsl.transparency,
	};
};

const hex2hsl = (color: Color): IHSLColor => {
	const hsl = ColorUtils.hex2hsl(color.hex);

	return {
		hue: hsl.h,
		saturation: hsl.s,
		lightness: hsl.l,
		transparency: color.transparency,
	};
};

export const ViewColorIconSelectorV5 = ({
	view,
	color,
	onColorChange,
	onIconClick,
	onCloseClick,
	isOpen,
	elementDivRef,
	horizontalAlignment = HorizontalAlignment.outerRight,
	verticalAlignment = VerticalAlignment.bottom,
	offsetX = -28,
	offsetY = 32,
	panelWidth = 272,
	isMiniColorPalette = true,
}: IViewColorIconSelectorProps) => {
	const [activeMode, setActiveMode] = useState<ColorWindowMode>("basic");
	const [colorAsHSL, setColorAsHSL] = useState<IHSLColor>(hex2hsl(color));
	const appState = useAppStore((state) => state.appState);

	const app = appState.app;
	const _floating = useRef<HTMLDivElement>(null);

	const onColorChangeLocal = (newColor: IHSLColor) => {
		const newColorAsHex = hsl2hex(newColor);
		setColorAsHSL(newColor);

		if (newColorAsHex.hex !== color.hex || newColorAsHex.transparency !== color.transparency) {
			onColorChange(hsl2hex(newColor));
		}
	};

	const onClickOutside = () => {
		setActiveMode("basic");
		onCloseClick();
	};

	useClickOutside([elementDivRef, _floating], onClickOutside);
	// Persist before an enclosing view popout can unmount the selector on the same mousedown.
	useClickOutside([elementDivRef, _floating], onClickOutside, "mousedown", [], true);
	useCloseOnKeyboardPress(onClickOutside, KeyboardListener.KEY_ESCAPE, [isOpen], {
		enabled: isOpen,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	useEffect(() => {
		if (elementDivRef.current && _floating.current) {
			const newTransformObject = DomUtils.getFixedFloatingElementPosition(
				elementDivRef.current,
				_floating.current,
				verticalAlignment,
				horizontalAlignment,
				offsetY,
				offsetX,
			);

			const colorWindowStyle: Partial<CSSStyleDeclaration> = {
				display: "",
			};

			if (!isOpen) {
				colorWindowStyle.display = "none";
			} else {
				const {x, y} = newTransformObject;
				colorWindowStyle.transform = `translate(${x}px, ${y}px)`;
			}

			if (_floating.current.style.transform !== colorWindowStyle.transform) {
				_floating.current.style.transform = colorWindowStyle.transform;
			}

			if (_floating.current.style.display !== colorWindowStyle.display) {
				_floating.current.style.display = colorWindowStyle.display;
			}
		}
	});

	return (
		<>
			{isOpen && (
				<DomPortal destination={app.modalContainer}>
					<ColorWindowV5
						divRef={_floating}
						activeMode={activeMode}
						setActiveMode={setActiveMode}
						onCloseClick={onCloseClick}
						onColorChange={onColorChangeLocal}
						isTransparencyEnabled={false}
						colorHSL={hsl2hex(colorAsHSL).hex !== color.hex ? hex2hsl(color) : colorAsHSL}
						isMiniColorPalette={isMiniColorPalette}
						view={view}
						onIconClick={onIconClick}
						panelWidth={panelWidth}
						closeOnColorSelect={false}
					/>
				</DomPortal>
			)}
		</>
	);
};
